import type { Id } from "@repo/convex/dataModel";
import { useCallback, useRef, useState } from "react";
import { useProjectDrag } from "../contexts/project-drag-context";

export const PROJECT_DRAG_MIME = "application/x-df-project";

interface UseDropTargetOptions {
  folderId: Id<"projectFolders"> | undefined; // undefined = "My Projects"
  onDrop: (projectId: Id<"projects">, folderId: Id<"projectFolders"> | undefined) => void;
}

export function useDropTarget({ folderId, onDrop }: UseDropTargetOptions) {
  const { isDragging, projectId, sourceFolderId } = useProjectDrag();
  const [isOver, setIsOver] = useState(false);
  const enterCountRef = useRef(0);

  // Valid target = drag active AND not the folder the project already lives in
  const isValidTarget = isDragging && folderId !== sourceFolderId;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(PROJECT_DRAG_MIME)) return;
    e.preventDefault();
    enterCountRef.current += 1;
    if (enterCountRef.current === 1) {
      setIsOver(true);
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(PROJECT_DRAG_MIME)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = isValidTarget ? "move" : "none";
    },
    [isValidTarget]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(PROJECT_DRAG_MIME)) return;
    enterCountRef.current -= 1;
    if (enterCountRef.current === 0) {
      setIsOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(PROJECT_DRAG_MIME)) return;
      e.preventDefault();
      enterCountRef.current = 0;
      setIsOver(false);
      if (isValidTarget && projectId) {
        onDrop(projectId, folderId);
      }
    },
    [isValidTarget, projectId, folderId, onDrop]
  );

  return {
    isOver,
    isValidTarget,
    isDragActive: isDragging,
    dropTargetProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
